import React from 'react';
import { Alert, Box, Chip, CircularProgress, Paper, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import { Bar, BarChart, CartesianGrid, Cell, LabelList, ReferenceLine, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { DEEP_GREEN, MUTED } from '../colorTokens';
import { VERDICT_COLOR, formatDay, formatNumber, formatPercent } from './format';
import { Caveat, NUMERALS } from './ScoreParts';
import { useCommitment } from './useReleaseCadence';

const CARD_SX = { borderRadius: '12px', borderColor: 'rgba(0,0,0,0.05)' };
const HEAD_SX = { '& th': { fontWeight: 600, color: DEEP_GREEN, whiteSpace: 'nowrap' } };
const STATUS = {
  met: { label: 'Met', color: VERDICT_COLOR.go },
  missed: { label: 'Missed', color: VERDICT_COLOR.hold },
  thin: { label: 'Too few instances', color: VERDICT_COLOR.na }
};

const weekLabel = (w) => formatDay(w.weekStart);
const statusOf = (w) => (w.inProgress && w.status !== 'thin' ? { label: 'In progress', color: VERDICT_COLOR.caution } : STATUS[w.status]);

const Stat = ({ label, value, hint }) => (
  <Tooltip title={hint || ''} placement="top" disableHoverListener={!hint}>
    <Box sx={{ pr: 3, mr: 3, borderRight: '1px solid rgba(0,0,0,0.08)', '&:last-of-type': { border: 0, mr: 0, pr: 0 } }}>
      <Typography sx={{ fontSize: '1.5rem', fontWeight: 700, color: DEEP_GREEN, lineHeight: 1.1, ...NUMERALS }}>{value}</Typography>
      <Typography variant="caption" sx={{ color: MUTED }}>{label}</Typography>
    </Box>
  </Tooltip>
);

const WeekTip = ({ active, payload }) => {
  if (!active || !payload || !payload.length) return null;
  const w = payload[0].payload;
  return (
    <Paper elevation={4} sx={{ p: 1.25, borderRadius: '10px' }}>
      <Typography sx={{ fontWeight: 700, color: DEEP_GREEN }}>Weekend of {formatDay(w.weekStart)}</Typography>
      <Typography variant="body2">{formatPercent(w.failureRate, 2)} failed first time ({formatNumber(w.failed)} of {formatNumber(w.instances)})</Typography>
      <Typography variant="caption" sx={{ color: MUTED, display: 'block' }}>{statusOf(w).label}</Typography>
    </Paper>
  );
};

const WeeklyChart = ({ weeks, threshold }) => {
  const max = Math.max(threshold * 2, ...weeks.map((w) => w.failureRate || 0));
  return (
    <Box sx={{ height: 260, mt: 1 }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart data={weeks} margin={{ top: 22, right: 24, left: 0, bottom: 0 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(0,0,0,0.08)" />
          <XAxis dataKey={weekLabel} tick={{ fontSize: 11, fill: MUTED }} tickLine={false} axisLine={{ stroke: 'rgba(0,0,0,0.15)' }} interval={0} />
          <YAxis domain={[0, max]} tickFormatter={(v) => `${Math.round(v * 1000) / 10}%`} tick={{ fontSize: 11, fill: MUTED }} tickLine={false} axisLine={false} width={44} />
          <ChartTooltip content={<WeekTip />} cursor={{ fill: 'rgba(26,46,34,0.05)' }} />
          <ReferenceLine y={threshold} stroke={VERDICT_COLOR.hold} strokeDasharray="6 4" label={{ value: `${threshold * 100}% commitment`, position: 'insideTopRight', fill: VERDICT_COLOR.hold, fontSize: 11 }} />
          <Bar dataKey="failureRate" maxBarSize={44} isAnimationActive={false} radius={[4, 4, 0, 0]}>
            {weeks.map((w) => (
              <Cell key={w.weekStart} fill={statusOf(w).color} fillOpacity={w.status === 'thin' || w.inProgress ? 0.45 : 1} />
            ))}
            <LabelList dataKey="failureRate" position="top" offset={6} formatter={(v) => (v == null ? '' : formatPercent(v, 2))} style={{ fontSize: 11, fontWeight: 600, fill: DEEP_GREEN }} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </Box>
  );
};

const typeRate = (t) => (t.instances ? `${formatPercent(t.failureRate, 2)} (${formatNumber(t.instances)})` : '–');

const WeekTable = ({ weeks }) => (
  <Paper variant="outlined" sx={{ ...CARD_SX, overflowX: 'auto' }}>
    <Table size="small">
      <TableHead>
        <TableRow sx={HEAD_SX}>
          <TableCell>Weekend of</TableCell>
          <TableCell>Versions</TableCell>
          <TableCell align="right">Instances</TableCell>
          <TableCell align="right">Failed first time</TableCell>
          <TableCell align="right">Failure rate</TableCell>
          <TableCell align="right">Still failing</TableCell>
          <TableCell align="right">Rolling upgrade share</TableCell>
          <TableCell align="right">Rolling failure rate</TableCell>
          <TableCell align="right">Standard failure rate</TableCell>
          <TableCell>Result</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {[...weeks].reverse().map((w) => {
          const s = statusOf(w);
          return (
            <TableRow key={w.weekStart} hover>
              <TableCell sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>{formatDay(w.weekStart)}{w.startsAtHistoryEdge ? ' *' : ''}</TableCell>
              <TableCell>{w.versions.slice(0, 3).map((v) => v.version).join(', ')}{w.versions.length > 3 ? ` +${w.versions.length - 3}` : ''}</TableCell>
              <TableCell align="right" sx={NUMERALS}>{formatNumber(w.instances)}</TableCell>
              <TableCell align="right" sx={NUMERALS}>{formatNumber(w.failed)}</TableCell>
              <TableCell align="right" sx={{ ...NUMERALS, fontWeight: 600 }}>{formatPercent(w.failureRate, 2)}</TableCell>
              <TableCell align="right" sx={NUMERALS}>{formatNumber(w.stillFailing)}</TableCell>
              <TableCell align="right" sx={NUMERALS}>{formatPercent(w.rolling.share)}</TableCell>
              <TableCell align="right" sx={NUMERALS}>{typeRate(w.rolling)}</TableCell>
              <TableCell align="right" sx={NUMERALS}>{typeRate(w.standard)}</TableCell>
              <TableCell>
                <Chip size="small" label={s.label} sx={{ height: 22, borderRadius: '8px', fontWeight: 600, bgcolor: `${s.color}1f`, color: s.color }} />
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  </Paper>
);

const CommitmentView = () => {
  const { data, loading, error, refreshError } = useCommitment();
  if (loading && !data) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!data) return null;
  const { weeks, summary, threshold, minInstances, history } = data;
  if (!weeks.length) return <Alert severity="info">No deployment runs recorded yet, so no week can be measured. Sync from Datadog first.</Alert>;
  const latest = summary.latest;

  return (
    <Box sx={{ display: 'grid', gap: 2.5 }}>
      {refreshError && <Alert severity="warning">Could not refresh: {refreshError}. Showing the last data loaded.</Alert>}
      <Paper variant="outlined" sx={{ ...CARD_SX, p: 3 }}>
        <Typography sx={{ fontWeight: 600, color: DEEP_GREEN }}>Weekly failure rate against the {threshold * 100}% commitment</Typography>
        <Typography variant="body2" sx={{ color: MUTED, mb: 2 }}>
          Share of instances whose first upgrade attempt of the weekend failed, even if a retry fixed it. The commitment is to stay under {threshold * 100}% every week.
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', rowGap: 2, mb: 1 }}>
          <Stat label={latest ? `Last full weekend (${formatDay(latest.weekStart)})` : 'Last full weekend'} value={latest ? formatPercent(latest.failureRate, 2) : '–'} hint="First-attempt failure rate of the most recent completed week with enough instances." />
          <Stat label="Weeks under the line" value={`${summary.weeksMet} of ${summary.weeksGraded}`} hint={`Completed weeks with at least ${minInstances} instances.`} />
          <Stat label="Current run of weeks under the line" value={summary.currentStreak} />
          <Stat label="Instances upgraded with rolling upgrade" value={formatPercent(summary.rollingShare)} hint="Share across all measured weeks. Rolling upgrade is the zero-downtime path." />
        </Box>
        <WeeklyChart weeks={weeks} threshold={threshold} />
      </Paper>
      <WeekTable weeks={weeks} />
      <Caveat>
        A week runs Saturday to Friday (UTC) and is named after its Saturday. Skipped runs and prerelease versions are left out, and weeks under {minInstances} instances are shown but not graded.
        Failed means Failed, VerificationFailed, TimeLapse, AppBackupFailed or any Rollback status. Still failing = no successful run that week. Data covers {formatNumber(history.runs)} recorded runs from {formatDay(history.earliest)} to {formatDay(history.latest)}.
        {weeks.some((w) => w.startsAtHistoryEdge) ? ' * The first week starts at the edge of the recorded history and may be missing its first runs.' : ''}
        {' '}Microservice upgrades are not included because Datadog has no data for them yet.
      </Caveat>
    </Box>
  );
};

export default CommitmentView;
